import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { PromoOfficeTender } from "@shared-core/data/adapter";
import {
  formatCurrencyGbp,
  formatLongDateOrDash,
} from "@shared-core/domain/formatters";
import { quotePriceColumns } from "@shared-core/domain/promo-office-pricing";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { Button } from "../app/components/primitives/button";
import { Loader2, Printer } from "lucide-react";

// Customer-facing quote preview/print page — sell pricing only, never cost
// or margin. Reached via "Generate customer quote" on an open Tender.
//
// A real PDF-generation service is backend work that doesn't exist yet for
// this feature, so this mocks the interaction with the browser's native
// print dialog ("Print / Save as PDF") rather than pretending a document
// service is wired up. Outside the dashboard shell for a clean print
// layout — see routes.ts for why it still requires sign-in.

// Guarded for the same reason as the quotes list: Promo Office's year-0001 "no
// date" sentinel arrives as null, and `new Date(null)` is the Unix epoch -- which
// on this page would print "1 January 1970" on a document a customer sees.
// The em-dash swap covers an unparseable value as well as null, for the same
// reason as the quotes list: shared-core returns its own "-" for both.
function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const formatted = formatLongDateOrDash(iso);
  return formatted === "-" ? "—" : formatted;
}

export function PromoOfficeQuotePrintPage() {
  const { tenderId } = useParams<{ tenderId: string }>();
  const dataAdapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const [tender, setTender] = useState<PromoOfficeTender | null | "loading">(
    "loading",
  );

  useEffect(() => {
    if (!tenderId) return;
    let cancelled = false;
    dataAdapter
      .getPromoOfficeTenderById(tenderId)
      .then((result) => {
        if (!cancelled) setTender(result);
      })
      // A failed read lands on the same panel as a missing quote: this page
      // has one job and no retry affordance, so "we can't show you this
      // quote" is the whole message either way. Without the catch the
      // spinner below spun forever instead.
      .catch(() => {
        if (!cancelled) setTender(null);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, tenderId]);

  if (tender === "loading") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!tender) {
    return (
      <div className="flex items-center justify-center min-h-screen text-center px-4">
        <div>
          <h2 className="mb-2">Quote not found</h2>
          <p className="text-muted-foreground text-sm">
            This quote may no longer be open, or the link is incorrect.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="flex items-center justify-between mb-8 print:hidden">
        <p className="text-sm text-muted-foreground">
          Customer-facing preview — sell pricing only, no cost/margin.
        </p>
        <Button size="sm" onClick={() => capabilities.printCurrentPage()}>
          <Printer className="size-4 mr-1.5" />
          Print / Save as PDF
        </Button>
      </div>

      <div className="space-y-1 mb-8">
        <h1>Quote {tender.tenderNumber}</h1>
        <p className="text-sm text-muted-foreground">
          {formatDate(tender.createdAt)}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-6 mb-8 text-sm">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
            Customer
          </div>
          <div>{tender.customerName ?? "—"}</div>
          {tender.customerReference && (
            <div className="text-muted-foreground">
              Ref: {tender.customerReference}
            </div>
          )}
        </div>
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
            Delivery address
          </div>
          <div>{tender.deliveryAddress ?? "—"}</div>
        </div>
      </div>

      <div className="space-y-6">
        {tender.lines.map((line) => (
          <div
            key={line.id}
            className="flex gap-4 pb-6 border-b border-border last:border-0"
          >
            {line.imageUrl ? (
              <img
                src={line.imageUrl}
                alt={line.description}
                className="size-24 rounded-md object-cover border border-border shrink-0"
              />
            ) : (
              <div className="size-24 rounded-md border border-border bg-muted shrink-0" />
            )}
            <div className="flex-1 space-y-2">
              <div className="text-xs text-muted-foreground font-mono">
                {line.articleNumber ?? "—"}
              </div>
              <div className="font-medium">{line.description}</div>
              <div className="overflow-x-auto">
                <table className="text-sm w-full">
                  <tbody>
                    <tr className="text-muted-foreground">
                      <td className="pr-4 py-1 font-medium text-foreground">
                        Quantity
                      </td>
                      {quotePriceColumns(line).map((column) => (
                        <td
                          key={column.quantity}
                          className="text-right px-3 py-1"
                        >
                          {column.quantity}
                        </td>
                      ))}
                    </tr>
                    <tr className="text-muted-foreground">
                      <td className="pr-4 py-1 font-medium text-foreground">
                        Unit price
                      </td>
                      {quotePriceColumns(line).map((column) => (
                        <td
                          key={column.quantity}
                          className="text-right px-3 py-1"
                        >
                          {formatCurrencyGbp(column.sellUnitPrice, 2)}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-t border-border font-medium">
                      <td className="pr-4 py-1">Subtotal</td>
                      {quotePriceColumns(line).map((column) => (
                        <td
                          key={column.quantity}
                          className="text-right px-3 py-1"
                        >
                          {formatCurrencyGbp(
                            column.sellUnitPrice * column.quantity,
                            2,
                          )}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
